import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Modal, Text, Input, Select, Button } from 'v3/components';
import { Col, Row } from 'react-bootstrap';
import { useTravel } from 'v3/pages/Travel';
import { FaTrash } from 'react-icons/fa';
import { useSnackbar } from 'v3/components/Snackbar';
import { CPF_MASK } from 'v3/utils/masks';
import { cooperplaceApi } from 'services/api';
import cloneDeep from 'lodash/cloneDeep';
import { useRiskManager } from '../context';

export function EscortModal({ disabled }) {
  const travel = useTravel();
  const { escortModal } = useRiskManager();
  const params = useParams();

  const snackbar = useSnackbar();

  const EscortType = [
    { name: 'Armada', slug: 'ARMED' },
    { name: 'Velada', slug: 'VEILED' },
  ];

  const [escortList, setEscortList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const isFormatCpf = (document) => {
    const regex = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
    return regex.test(document);
  };

  const isVehiclePlate = (plate) => {
    const regex = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/;
    return regex.test(plate.toUpperCase());
  };

  useEffect(() => {
    if (travel?.data?.travelEscort) {
      setEscortList(cloneDeep(travel.data.travelEscort));
    }
  }, [travel?.data?.travelEscort]);

  const handleAddEscort = () => {
    setEscortList((prev) => [
      ...prev,
      { agentName: '', agentCpf: '', vehicle: '', escortType: null },
    ]);
  };

  const handleRemoveEscort = (index) => {
    setEscortList((prev) => prev.filter((_, i) => i !== index));

    setErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      newErrors.splice(index, 1);
      return newErrors;
    });
  };

  const handleInputChange = (index, field, value) => {
    const updated = [...escortList];
    updated[index][field] = value;
    setEscortList(updated);
  };

  const handleClose = () => {
    escortModal.close();

    setErrors([]);
    setEscortList(cloneDeep(travel?.data?.travelEscort || []));
  };

  function validateEscort(data) {
    const errors = [];

    data.forEach((item, index) => {
      const itemErrors = {};

      if (!item.agentName || item.agentName.trim() === '') {
        itemErrors.agentName = 'Nome obrigatório';
      }

      if (!item.agentCpf || item.agentCpf.trim() === '') {
        itemErrors.agentCpf = 'CPF obrigatório';
      } else if (!isFormatCpf(item.agentCpf)) {
        itemErrors.agentCpf = 'CPF inválido';
      }

      if (!item.vehicle || item.vehicle.trim() === '') {
        itemErrors.vehicle = 'Placa obrigatória';
      } else if (!isVehiclePlate(item.vehicle)) {
        itemErrors.vehicle = 'Placa inválida';
      }

      if (!item.escortType || item.escortType === '') {
        itemErrors.escortType = 'Tipo obrigatório';
      }

      errors[index] = itemErrors;
    });

    const hasErrors = errors.some((item) => Object.keys(item).length > 0);

    return [!hasErrors, errors];
  }

  const handleSave = async (data) => {
    setLoading(true);

    try {
      setErrors([]);

      const [isValid, validationErrors] = validateEscort(data);

      if (isValid !== true) {
        setErrors(validationErrors);

        snackbar.show(<Text>Erro ao cadastrar escolta</Text>, {
          type: 'error',
        });

        return;
      }

      await cooperplaceApi.put(`travels/escort/${params.id}`, {
        data,
      });

      travel.fetchTravel();

      handleClose();
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      backdrop="static"
      closeButton
      handleClose={handleClose}
      size="lg"
      centered
      scrollable
      heading={
        <Row className="d-flex justify-content-between align-items-center">
          <Col>
            <Text color="dark" type="header">
              Escoltas
            </Text>
          </Col>
        </Row>
      }
      show={escortModal.isOpen}
      body={
        <Row style={{ display: 'flex', alignItems: 'center' }}>
          {escortList.length > 0 ? (
            escortList.map((item, index) => (
              <React.Fragment key={index}>
                <Col xs={3} className="mb-3">
                  <Input
                    label="Nome do agente"
                    value={item.agentName}
                    onChange={(e) => handleInputChange(index, 'agentName', e.target.value)}
                    error={errors?.[index]?.agentName}
                    disabled={disabled}
                  />
                </Col>
                <Col xs={3} className="mb-3">
                  <Input
                    label="CPF do agente"
                    value={item.agentCpf}
                    onChange={(e) => handleInputChange(index, 'agentCpf', e.target.value)}
                    masked
                    mask={CPF_MASK}
                    guide={false}
                    error={errors?.[index]?.agentCpf}
                    disabled={disabled}
                  />
                </Col>
                <Col xs={2} className="mb-3">
                  <Input
                    label="Placa do veículo"
                    value={item.vehicle}
                    onChange={(e) =>
                      handleInputChange(index, 'vehicle', e.target.value.toUpperCase())
                    }
                    error={errors?.[index]?.vehicle}
                    disabled={disabled}
                  />
                </Col>
                <Col xs={3} className="mb-3">
                  <Select
                    label="Tipo de escolta"
                    options={EscortType}
                    value={EscortType.find((opt) => opt.slug == item.escortType)}
                    onChange={(value) => handleInputChange(index, 'escortType', value?.slug)}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.slug}
                    error={errors?.[index]?.escortType}
                    isDisabled={disabled}
                  />
                </Col>
                {!disabled && (
                  <Col xs={1} className="pt-3">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemoveEscort(index)}
                      title="Remover agente"
                    >
                      <FaTrash size={18} />
                    </Button>
                  </Col>
                )}
              </React.Fragment>
            ))
          ) : (
            <Text>Nenhuma escolta cadastrada</Text>
          )}
        </Row>
      }
      footer={
        !disabled && (
          <div className="d-flex justify-content-end align-items-center flex-nowrap" style={{ gap: '0.5rem' }}>
            <Button variant="secondary" className="py-2" onClick={handleAddEscort}>
              <Text color="dark" type="regular" weight="500">
                Adicionar
              </Text>
            </Button>
            <Button
              variant="primary"
              className="py-2"
              onClick={() => handleSave(escortList)}
              loading={loading}
              disabled={travel?.data?.travelEscort?.length === 0 && escortList?.length === 0}
            >
              <Text color="white" type="regular" weight="500">
                Salvar
              </Text>
            </Button>
          </div>
        )
      }
    />
  );
}
